import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, merge, Subject, timer } from 'rxjs';
import { debounceTime, switchMap, takeUntil, throttleTime } from 'rxjs/operators';
import { ToastrMessageComponent } from '../../commonComponent/formComponent/toastr-message/toastr-message.component';
import { AuthApis } from '../../../services/authApis';

@Injectable({ providedIn: 'root' })
export class IdleService implements OnDestroy {
  private readonly TIMEOUT_MS = 15 * 60 * 1000;
  private readonly DEBOUNCE_MS = 2000;
  private readonly THROTTLE_MS = 1000;

  private destroy$ = new Subject<void>();
  private watching = false;

  constructor(
    private toastr: ToastrMessageComponent,
    private auth: AuthApis,
    private router: Router,
    private ngZone: NgZone
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startWatching() {
    if (this.watching) return;
    this.watching = true;

    this.ngZone.runOutsideAngular(() => {
      const keyPress$ = fromEvent(document, 'keydown');
      const click$ = fromEvent(document, 'click');
      const scroll$ = fromEvent(document, 'scroll');
      const touch$ = fromEvent(document, 'touchstart');

      merge(keyPress$, click$, scroll$, touch$)
        .pipe(
          throttleTime(this.THROTTLE_MS),
          debounceTime(this.DEBOUNCE_MS),
          switchMap(() => timer(this.TIMEOUT_MS)),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.ngZone.run(() => this.logout());
        });
    });
  }

  stopWatching() {
    if (!this.watching) return;
    this.watching = false;
    this.destroy$.next();
  }

  async logout() {
    await this.auth.logout();
  }

  private clearSessionData(): void {
    const keysToClear = ['token', 'isLoggedIn'];
    keysToClear.forEach((key) => localStorage.removeItem(key));
  }
}
